import { useCallback, useEffect, useState } from 'react';
import { queryCache, useQuery } from 'react-query';
import { getAssessment } from 'src/api/assessment';
import { QKeys, Variable } from 'src/api/types';
import _ from 'lodash';
import { StandardVariableValues } from './types';
import { genStandardVariableValues } from './util';
import { useAuth } from 'src/state/auth';
import { useCache } from 'src/graphql';

type UseVariables = (a: { assessmentId?: string; wvOverride?: boolean }) => {
  variables: Variable[] | null;
  isLoading: boolean;
  containsUndefinedVariableFromFormula: (expression: string) => boolean;
  standardVariableValues: StandardVariableValues | null;
  invalidate: (invalidateAssessment?: boolean) => Promise<void>;
};

const useVariables: UseVariables = ({ assessmentId, wvOverride }) => {
  const [variables, setVariables] = useState<Variable[] | null>(null);
  const [standardVariableValues, setStandardVariableValues] = useState<StandardVariableValues | null>(null);
  const { variables: wVariables } = useAuth();
  const { refetchQuery } = useCache();

  const { data, isLoading } = useQuery([QKeys.Assessment, { assessmentId }], getAssessment, {
    enabled: !!assessmentId,
  });

  const invalidate = async () => {
    await refetchQuery({ include: ['profile'] });
    await queryCache.invalidateQueries([QKeys.Assessment, { assessmentId }]);
  };

  useEffect(() => {
    if (!isLoading && !!wVariables) {
      const assessmentVariables = data?.variables || [];

      const allVariables = _.sortBy([...assessmentVariables, ...(wVariables || [])], (el) =>
        el.alias ? el.alias.toLowerCase() : el.name.toLowerCase(),
      );

      setVariables(allVariables);

      if (wVariables) {
        const gen = genStandardVariableValues(wVariables);
        setStandardVariableValues(gen);
      }
    }
  }, [data, isLoading, wvOverride, wVariables]);

  const containsUndefinedVariableFromFormula = useCallback(
    (expression: string) => {
      const variableIds = expression.match(/#\d+/g);
      return !!variableIds?.some((id) => variables?.find((v) => v.id === parseInt(id.slice(1)))?.value === null);
    },
    [variables],
  );

  return {
    variables,
    isLoading: isLoading || !standardVariableValues,
    containsUndefinedVariableFromFormula,
    standardVariableValues,
    invalidate,
  };
};

export default useVariables;
